import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface WebhookResponse {
  url?: string;
  endpoint?: string;
  tokenName?: string | null;
  tokenRotatedAtUtc?: string | null;
  endpointValidatedAtUtc?: string | null;
}

export interface WebhookTokenResponse {
  token: string;
}

export interface WebhookSettings {
  endpoint: string | null;
  tokenName: string | null;
  tokenRotatedAtUtc: string | null;
  endpointValidatedAtUtc: string | null;
}

class WebhooksService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // GET retrieves existing webhook URL - response can be string or object with url property
  async getWebhook(): Promise<WebhookSettings> {
    try {
      const response = await axios.get<string | WebhookResponse>(`${API_CONFIG.BASE_URL}/webhooks`, {
        headers: this.getAuthHeaders(),
      });

      if (typeof response.data === 'string') {
        return {
          endpoint: response.data,
          tokenName: null,
          tokenRotatedAtUtc: null,
          endpointValidatedAtUtc: null,
        };
      }

      return {
        endpoint: response.data.endpoint || response.data.url || null,
        tokenName: response.data.tokenName?.trim() || null,
        tokenRotatedAtUtc: response.data.tokenRotatedAtUtc?.trim() || null,
        endpointValidatedAtUtc: response.data.endpointValidatedAtUtc?.trim() || null,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return {
          endpoint: null,
          tokenName: null,
          tokenRotatedAtUtc: null,
          endpointValidatedAtUtc: null,
        };
      }
      console.error('Error fetching webhook:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  // POST sets/updates webhook URL (payload is just the URL as string)
  async setWebhook(url: string): Promise<void> {
    try {
      await axios.post(`${API_CONFIG.BASE_URL}/webhooks`, url, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error setting webhook:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  // DELETE deletes existing webhook URL
  async deleteWebhook(): Promise<void> {
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/webhooks`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting webhook:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  // POST generates webhook HMAC signature token - response can be string or object with token property
  async generateWebhookToken(): Promise<string> {
    try {
      const response = await axios.post<string | WebhookTokenResponse>(`${API_CONFIG.BASE_URL}/webhooks/generate-token`, {}, {
        headers: this.getAuthHeaders(),
      });
      // Handle both string response and object response
      if (typeof response.data === 'string') {
        return response.data;
      }
      return response.data.token;
    } catch (error) {
      console.error('Error generating webhook token:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }
}

export const webhooksService = new WebhooksService();

